/**
 * The pure half of "put Fint on your home screen": what kind of device this
 * is, whether the app is already running as an installed one, and whether the
 * banner is due. Nothing here touches a global - every function is handed
 * what it reads - so the unit test runs under plain Node.
 */

/**
 * Where the person is, as far as installing goes.
 *
 * Not "which browser" - on iPhone and iPad every browser is WebKit and every
 * one installs the same way, through the Share sheet, because Apple gives
 * none of them `beforeinstallprompt`. On Android and on a laptop the browser
 * matters and is asked directly: if it fires the event we can prompt, and if
 * it does not, the instructions are its own menu's.
 */
export type InstallPlatform = 'iphone' | 'ipad' | 'android' | 'desktop';

/**
 * `maxTouchPoints` is how an iPad is told from a Mac: since iPadOS 13 Safari
 * on an iPad calls itself "Macintosh" so that sites serve it the desktop
 * page, and the only thing left that says "this is a tablet" is that it has
 * a touch screen and a Mac does not.
 */
export function detectPlatform(userAgent: string, maxTouchPoints = 0): InstallPlatform {
  if (/iPhone|iPod/i.test(userAgent)) return 'iphone';
  if (/iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)) return 'ipad';
  if (/Android/i.test(userAgent)) return 'android';
  return 'desktop';
}

/**
 * Whether this page is the installed app rather than a browser tab.
 *
 * The media query is the standard; `navigator.standalone` is Safari's older
 * spelling of the same fact and is still the one iOS answers reliably.
 * `window-controls-overlay` is the desktop variant a person opts into after
 * installing, and it is no less installed.
 */
export function isStandalone(
  matchMedia: ((query: string) => { matches: boolean }) | undefined,
  navigator: { standalone?: boolean } | undefined,
): boolean {
  if (matchMedia) {
    for (const mode of ['standalone', 'window-controls-overlay', 'fullscreen', 'minimal-ui']) {
      if (matchMedia(`(display-mode: ${mode})`).matches) return true;
    }
  }
  return navigator?.standalone === true;
}

/** The localStorage subset the scheduling reads and writes. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Page loads counted so far. One per boot of the app, not one per route. */
export const INSTALL_VISITS_KEY = 'fint-install-visits';
/** When the banner was last shown, whether or not it was dismissed. */
export const INSTALL_BANNER_SEEN_KEY = 'fint-install-banner-seen';
/** Set on `appinstalled`, so the banner never returns in a browser tab on a device that has the app. */
export const INSTALL_INSTALLED_KEY = 'fint-installed';

/**
 * Not before the third visit: somebody who opened Fint once from a link has
 * not decided anything yet, and a banner on a first visit is the thing
 * people learn to close without reading. The third time is a habit.
 */
export const INSTALL_BANNER_AFTER_VISITS = 3;
export const INSTALL_BANNER_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function readNumber(storage: StorageLike, key: string): number {
  try {
    const n = Number(storage.getItem(key));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function write(storage: StorageLike, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Private mode, or storage disabled. Then there is no count and no
    // banner, which is the quiet failure.
  }
}

/** Counts this boot of the app. Returns the new total. */
export function recordVisit(storage: StorageLike): number {
  const visits = readNumber(storage, INSTALL_VISITS_KEY) + 1;
  write(storage, INSTALL_VISITS_KEY, String(visits));
  return visits;
}

/**
 * Whether the banner is due now.
 *
 * Never in the installed app - it would be asking someone to do what they
 * have done. Never once `appinstalled` has fired on this device. Otherwise
 * from the third visit, and then not again for thirty days after it was last
 * shown: shown, not dismissed, so ignoring it counts. A banner that comes
 * back on every visit until you find its close button is a nag, and a nag is
 * how a person learns to distrust everything else the app says.
 */
export function shouldShowInstallBanner(storage: StorageLike, now: number, standalone: boolean): boolean {
  if (standalone) return false;
  if (readNumber(storage, INSTALL_INSTALLED_KEY)) return false;
  if (readNumber(storage, INSTALL_VISITS_KEY) < INSTALL_BANNER_AFTER_VISITS) return false;
  const seenAt = readNumber(storage, INSTALL_BANNER_SEEN_KEY);
  return !seenAt || now - seenAt >= INSTALL_BANNER_SNOOZE_MS;
}

/** The banner was shown (or closed) at `now`; not again for thirty days. */
export function markInstallBannerSeen(storage: StorageLike, now: number): void {
  write(storage, INSTALL_BANNER_SEEN_KEY, String(now));
}

/** The browser said the app was installed. */
export function markInstalled(storage: StorageLike): void {
  write(storage, INSTALL_INSTALLED_KEY, '1');
}

/**
 * What the Settings card and the banner say, per platform, when there is no
 * prompt to offer. The strings live here rather than in the components so
 * the e2e spec and the unit test read the same words the person does.
 */
export const INSTALL_INSTRUCTIONS: Record<InstallPlatform, { title: string; steps: string[] }> = {
  iphone: {
    title: 'Add Fint to your iPhone',
    steps: ['Tap Share at the bottom of Safari', 'Choose "Add to Home Screen"', 'Tap Add'],
  },
  ipad: {
    title: 'Add Fint to your iPad',
    steps: ['Tap Share at the top of Safari', 'Choose "Add to Home Screen"', 'Tap Add'],
  },
  android: {
    title: 'Add Fint to your home screen',
    steps: ['Open the browser menu (⋮)', 'Choose "Add to Home screen" or "Install app"'],
  },
  desktop: {
    title: 'Install Fint on this computer',
    steps: ['Use your browser’s Install option - in Chrome and Edge it is the icon at the right end of the address bar'],
  },
};

/** The name of the window event main.tsx fires when a new build has installed behind the running one. */
export const UPDATE_READY_EVENT = 'fint:update-ready';
